import React, { useState, useRef, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import ArrowDown from "components/svg/ArrowDown";
import CircleCloseFilter from "components/svg/CircleCloseFilter";
import classnames from "classnames";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { toggleNavigation } from "redux/actions/uiActions";
import { useIntl, FormattedMessage } from "react-intl";
import config from "config";
import uuid from "uuid";
import {
  FeatureToggle,
  FeatureOn,
} from "components/_FeatureToggle/FeatureToggle";
import { addCase } from "redux/thunks/caseThunks";
import ReactModal from "react-modal";
import { isUserAdmin } from "components/formcomponents/utils/userUtils";
import { usAdminAppRoutes } from "../../route-definitions";
import { USADMIN_ENTITIES, US_ADMIN_APP } from "../../constants/constants";

const linkConfigs = [
  {
    key: USADMIN_ENTITIES.END_USERS,
    path: usAdminAppRoutes.endUsers,
    intlId: "usAdmin.endUser",
  },
  {
    key: USADMIN_ENTITIES.VENDORS,
    path: usAdminAppRoutes.vendors,
    intlId: "usAdmin.vendor",
  },
  {
    key: USADMIN_ENTITIES.DISTRIBUTORS,
    path: usAdminAppRoutes.distributors,
    intlId: "usAdmin.distributor",
  },
  {
    key: USADMIN_ENTITIES.INDUSTRY_SPECIALISTS,
    path: usAdminAppRoutes.industrySpecialists,
    intlId: "usAdmin.industrySpecialist",
  },
  {
    key: USADMIN_ENTITIES.TERRITORY_MANAGERS,
    path: usAdminAppRoutes.territoryManagers,
    intlId: "usAdmin.territoryManager",
  },
];

const TheNavigation = (props) => {
  // state
  const [activeSubNavigation, setActiveSubNavigation] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const intl = useIntl();

  // methods
  const toggleSubItems = (parentName) => {
    setActiveSubNavigation(
      activeSubNavigation === parentName ? "" : parentName
    );
  };

  const closeSubItemIfOpen = (parentName) => {
    if (activeSubNavigation.includes(parentName)) {
      toggleSubItems(parentName);
    }
  };
  const toggleNavigation = () => props.toggleNavigation();

  const addOneCase = (id, confidential = false) => {
    setCreateModalOpen(true);
    const lang = localStorage.getItem("currentLang");
    props.addCase(id, confidential, lang, props.user, (caseData) =>
      props.history.push("/case/" + caseData.id)
    );
    toggleSubItems("Create new report");
  };

  function generateOfflineCaseId(id) {
    return id + "-" + uuid.v4() + "-offlineCase";
  }

  const navigationWrapperRef = useRef(null);

  const handleClickOutside = (event) => {
    if (
      navigationWrapperRef.current &&
      !navigationWrapperRef.current.contains(event.target)
    ) {
      if (activeSubNavigation !== "") {
        setActiveSubNavigation("");
        props.toggleNavigation();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const isInternalUser = (user) => !checkFuzzyBoolean(user["custom:external"]);
  const isAdministrator = (user) => isUserAdmin(user["custom:administrator"]);

  const isNAMUser = (user) => {
    if (user["custom:region"]) {
      return user["custom:region"] === "NAM";
    }
    return false;
  };

  const checkFuzzyBoolean = (value) => {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      return String(value).toLowerCase() === "true";
    }
  };

  const showUsAdmin = isNAMUser(props.user) && !isInternalUser(props.user);
  const showPortal =
    (isNAMUser(props.user) && isInternalUser(props.user)) ||
    isAdministrator(props.user);

  const isSKF = isInternalUser(props.user);
  const isNAM = isNAMUser(props.user);
  const isAdmin = isAdministrator(props.user);

  const canViewBDA = isSKF;
  const canViewWIND = isSKF;
  const canViewGBI = isSKF;
  const canViewBI = isSKF || !isNAM;
  const canViewUSInspect = isNAM || isAdmin;

  const appLinks = linkConfigs.map(({ key, path, intlId }) => (
    <Link
      key={key}
      to={path}
      className="navigation__sub-link"
      onClick={() => closeSubItemIfOpen(US_ADMIN_APP)}
    >
      {intl.formatMessage({ id: intlId })}
    </Link>
  ));

  return (
    <>
      <nav
        className={classnames("navigation", {
          "navigation--is-open": props.isNavigationOpen,
        })}
        ref={navigationWrapperRef}
      >
        <div className="navigation__container">
          <div className="navigation__header">
            <FormattedMessage id="navigation.menuLabel" />
            <button
              className="navigation__header-close-button"
              onClick={toggleNavigation}
            >
              <CircleCloseFilter />
            </button>
          </div>

          <div className="navigation__sub-navigation-container">
            <button
              id="nav__button__toggler"
              className={classnames(
                "navigation__action navigation__action--toggler",
                {
                  "navigation__action--active":
                    activeSubNavigation === "Create new report",
                }
              )}
              onClick={() => toggleSubItems("Create new report")}
            >
              <FormattedMessage id="navigation.createNewReport" />
              <ArrowDown className="navigation__action-svg-arrow" />
            </button>

            {activeSubNavigation === "Create new report" && (
              <div className="navigation__sub-navigation-items">
                {canViewBDA && (
                  <a
                    id="create__bda__button"
                    className="navigation__sub-link"
                    onClick={() => {
                      addOneCase(config.BART_TEMPLATE_ID);
                      toggleSubItems("Create new report");
                    }}
                  >
                    <FormattedMessage id="navigation.bearingDamageAnalysis" />
                  </a>
                )}

                {canViewBI && (
                  <a
                    id="create__bi__button"
                    className="navigation__sub-link"
                    onClick={() => {
                      toggleSubItems("Create new report");
                      addOneCase(config.INSPECTION_TEMPLATE_ID);
                    }}
                  >
                    <FormattedMessage id="navigation.bearingInspection" />
                  </a>
                )}

                {canViewUSInspect && (
                  <FeatureToggle feature="usinspection">
                    <FeatureOn>
                      <a
                        id="create__nam__button"
                        className="navigation__sub-link"
                        onClick={() => {
                          toggleSubItems("Create new report");
                          addOneCase(config.US_INSPECTION_TEMPLATE_ID);
                        }}
                      >
                        <FormattedMessage id="createReport.NAMInspection" />
                      </a>
                    </FeatureOn>
                  </FeatureToggle>
                )}

                {canViewWIND && (
                  <a
                    id="create__wind__button"
                    className="navigation__sub-link"
                    onClick={() => {
                      toggleSubItems("Create new report");
                      addOneCase(config.WIND_TEMPLATE_ID, true);
                    }}
                  >
                    <FormattedMessage id="navigation.wind" />
                  </a>
                )}

                {canViewGBI && (
                  <a
                    id="create__gbi__button"
                    className="navigation__sub-link"
                    onClick={() => {
                      toggleSubItems("Create new report");
                      addOneCase(config.GBI_TEMPLATE_ID);
                    }}
                  >
                    <FormattedMessage id="navigation.gbi" />
                  </a>
                )}
              </div>
            )}
          </div>

          {props.connection ? (
            <React.Fragment>
              <Link
                to={"/reports"}
                className="navigation__action"
                onClick={() => {
                  closeSubItemIfOpen("Create new report");
                  props.toggleNavigation();
                }}
              >
                <FormattedMessage id="pageName.reports" />
              </Link>
              {showPortal ? (
                <div className="navigation__sub-navigation-container">
                  <button
                    id="nav__button__toggler"
                    className={classnames(
                      "navigation__action navigation__action--toggler",
                      {
                        "navigation__action--active":
                          activeSubNavigation === US_ADMIN_APP,
                      }
                    )}
                    onClick={() => toggleSubItems(US_ADMIN_APP)}
                  >
                    <FormattedMessage id="usAdmin.usAdminApp" />
                    <ArrowDown className="navigation__action-svg-arrow" />
                  </button>
                  {activeSubNavigation === US_ADMIN_APP && (
                    <div className="navigation__sub-navigation-items">
                      {appLinks}
                    </div>
                  )}
                </div>
              ) : null}
            </React.Fragment>
          ) : null}
        </div>
      </nav>
      <ReactModal
        isOpen={createModalOpen}
        className="react-modal approveai-container"
        overlayClassName="react-modal-overlay"
      >
        <div className="react-modal__body update-modal__body">
          <h2>
            <FormattedMessage id="navigation.creatingReport" />
          </h2>
        </div>
      </ReactModal>
    </>
  );
};

TheNavigation.propTypes = {
  isNavigationOpen: PropTypes.bool.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleNavigation: () => dispatch(toggleNavigation()),
    addCase: (id, confidential, lang, user, callback) =>
      dispatch(addCase(id, confidential, lang, user, callback)),
  };
};

const mapStateToProps = (state) => {
  return {
    isNavigationOpen: state.ui.isNavigationOpen,
    connection: state.connection,
    user: state.currentUser,
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TheNavigation)
);
